import React, { useState } from 'react';
import {
  Box, TextField, Typography, Grid, Paper, Avatar, InputAdornment,
  List, ListItemButton, ListItemAvatar, ListItemText, Checkbox, Chip, Stack,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PercentIcon from '@mui/icons-material/Percent';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import InventoryIcon from '@mui/icons-material/Inventory';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import { FieldRow, useDesignTokens } from '../ui';
import { radius } from '../../themes/smartinventoryTheme';

// Content for the 5 wizard steps (Basics · Discount · Products · Users ·
// Expiry). Chrome (progress bars, Back/Next, discard-when-dirty confirm) is
// WizardDrawer — this only renders the active step's fields, same split as
// CategoryFormDrawer/ProductFormDrawer.
const CouponWizardDrawer = ({ t, activeStep, form, setForm, products, users, toggleProduct, toggleUser }) => {
  const { categoryColors } = useDesignTokens();
  const presets = Object.values(categoryColors)
    .filter((tone) => tone.dot !== categoryColors.none.dot)
    .map((tone) => tone.dot);
  const [productSearch, setProductSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');

  const filteredProducts = products.filter((p) => p.productName?.toLowerCase().includes(productSearch.toLowerCase()));
  const filteredUsers = users.filter((u) => u.username?.toLowerCase().includes(userSearch.toLowerCase()) || u.email?.toLowerCase().includes(userSearch.toLowerCase()));

  if (activeStep === 0) {
    return (
      <Box>
        <FieldRow label={t('Coupon Code')} required>
          <TextField
            fullWidth
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/\s/g, '') })}
            placeholder="SAVE20"
            helperText={t('This is what customers will enter at checkout')}
          />
        </FieldRow>

        <FieldRow label={t('Color')}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            {presets.map((hex) => {
              const selected = form.color?.toLowerCase() === hex.toLowerCase();
              return (
                <Box
                  key={hex}
                  onClick={() => setForm({ ...form, color: hex })}
                  sx={{
                    width: 32, height: 32, borderRadius: `${radius.sm}px`, bgcolor: hex, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '2px solid', borderColor: selected ? 'text.primary' : 'transparent',
                  }}
                >
                  {selected && <CheckIcon fontSize="small" sx={{ color: 'common.white' }} />}
                </Box>
              );
            })}
          </Box>
          <TextField
            fullWidth
            value={form.color}
            onChange={(e) => setForm({ ...form, color: e.target.value })}
            placeholder="#6366f1"
            InputProps={{
              startAdornment: (
                <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: form.color, mr: 1, flexShrink: 0, border: '1px solid', borderColor: 'divider' }} />
              ),
            }}
          />
        </FieldRow>

        <FieldRow label={t('Internal Description')} helper={t('Optional - for internal reference only')}>
          <TextField
            fullWidth
            value={form.internalDescription}
            onChange={(e) => setForm({ ...form, internalDescription: e.target.value })}
            multiline
            rows={3}
            placeholder={t("Add notes for your team (customers won't see this)")}
          />
        </FieldRow>
      </Box>
    );
  }

  if (activeStep === 1) {
    return (
      <Box>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Paper
              variant="outlined"
              sx={{
                p: 2, cursor: 'pointer',
                borderColor: form.discountType === 'PERCENT' ? 'primary.main' : 'divider',
                borderWidth: 2,
                bgcolor: form.discountType === 'PERCENT' ? (theme) => alpha(theme.palette.primary.main, 0.05) : 'transparent',
              }}
              onClick={() => setForm({ ...form, discountType: 'PERCENT' })}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar sx={{ bgcolor: form.discountType === 'PERCENT' ? 'primary.main' : 'grey.300' }}><PercentIcon /></Avatar>
                <Box flex={1}>
                  <Typography fontWeight={600}>{t('Percentage')}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('e.g., 20% off the total')}</Typography>
                </Box>
                {form.discountType === 'PERCENT' && <CheckCircleIcon color="primary" fontSize="small" />}
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper
              variant="outlined"
              sx={{
                p: 2, cursor: 'pointer',
                borderColor: form.discountType === 'FIXED' ? 'primary.main' : 'divider',
                borderWidth: 2,
                bgcolor: form.discountType === 'FIXED' ? (theme) => alpha(theme.palette.primary.main, 0.05) : 'transparent',
              }}
              onClick={() => setForm({ ...form, discountType: 'FIXED' })}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar sx={{ bgcolor: form.discountType === 'FIXED' ? 'primary.main' : 'grey.300' }}><AttachMoneyIcon /></Avatar>
                <Box flex={1}>
                  <Typography fontWeight={600}>{t('Fixed Amount')}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('e.g., $10 off the total')}</Typography>
                </Box>
                {form.discountType === 'FIXED' && <CheckCircleIcon color="primary" fontSize="small" />}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <FieldRow
          label={t('Discount Value')}
          required
          helper={form.discountType === 'PERCENT' ? t('Enter a value between 1 and 100') : t('Enter the fixed discount amount')}
        >
          <TextField
            fullWidth
            type="number"
            value={form.discountValue}
            onChange={(e) => setForm({ ...form, discountValue: e.target.value })}
            inputProps={{ min: 0, max: form.discountType === 'PERCENT' ? 100 : undefined, step: form.discountType === 'PERCENT' ? 1 : 0.01 }}
            InputProps={{
              startAdornment: <InputAdornment position="start">{form.discountType === 'PERCENT' ? '%' : '$'}</InputAdornment>,
            }}
          />
        </FieldRow>
      </Box>
    );
  }

  if (activeStep === 2) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('Select which products this coupon can be applied to. Leave empty for all products.')}
        </Typography>
        {form.applicableProducts.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
            {form.applicableProducts.map((product) => (
              <Chip key={product._id} label={product.productName} onDelete={() => toggleProduct(product)} color="primary" variant="outlined" size="small" />
            ))}
          </Box>
        )}
        <TextField
          fullWidth
          size="small"
          placeholder={t('Search products...')}
          value={productSearch}
          onChange={(e) => setProductSearch(e.target.value)}
          sx={{ mb: 1.5 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment> }}
        />
        <Paper variant="outlined" sx={{ maxHeight: 320, overflow: 'auto' }}>
          <List disablePadding>
            {filteredProducts.map((product) => {
              const isSelected = form.applicableProducts.some((p) => p._id === product._id);
              return (
                <ListItemButton key={product._id} onClick={() => toggleProduct(product)} selected={isSelected}>
                  <ListItemAvatar>
                    <Avatar variant="rounded" src={product.productImage || product.productImages?.[0]} sx={{ width: 40, height: 40, bgcolor: 'grey.200' }}>
                      <InventoryIcon color="action" fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={product.productName} secondary={`$${product.productPrice?.toFixed(2) || '0.00'}`} />
                  <Checkbox checked={isSelected} color="primary" edge="end" />
                </ListItemButton>
              );
            })}
            {filteredProducts.length === 0 && (
              <Box sx={{ p: 3, textAlign: 'center' }}><Typography color="text.secondary" variant="body2">{t('No products found')}</Typography></Box>
            )}
          </List>
        </Paper>
      </Box>
    );
  }

  if (activeStep === 3) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('Select which users can use this coupon. Leave empty for all users.')}
        </Typography>
        {form.eligibleUsers.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
            {form.eligibleUsers.map((user) => (
              <Chip key={user._id} label={user.username} onDelete={() => toggleUser(user)} color="primary" variant="outlined" size="small" />
            ))}
          </Box>
        )}
        <TextField
          fullWidth
          size="small"
          placeholder={t('Search users...')}
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          sx={{ mb: 1.5 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment> }}
        />
        <Paper variant="outlined" sx={{ maxHeight: 320, overflow: 'auto' }}>
          <List disablePadding>
            {filteredUsers.map((user) => {
              const isSelected = form.eligibleUsers.some((u) => u._id === user._id);
              return (
                <ListItemButton key={user._id} onClick={() => toggleUser(user)} selected={isSelected}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: isSelected ? 'primary.main' : 'grey.400' }}>{user.username?.charAt(0).toUpperCase()}</Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={user.username} secondary={user.email} />
                  <Checkbox checked={isSelected} color="primary" edge="end" />
                </ListItemButton>
              );
            })}
            {filteredUsers.length === 0 && (
              <Box sx={{ p: 3, textAlign: 'center' }}><Typography color="text.secondary" variant="body2">{t('No users found')}</Typography></Box>
            )}
          </List>
        </Paper>
      </Box>
    );
  }

  // Expiry + preview
  return (
    <Box>
      <Grid container spacing={1.5}>
        <Grid item xs={12}>
          <Paper
            variant="outlined"
            sx={{
              p: 2, cursor: 'pointer',
              borderColor: form.neverExpires ? 'primary.main' : 'divider',
              borderWidth: 2,
              bgcolor: form.neverExpires ? (theme) => alpha(theme.palette.primary.main, 0.05) : 'transparent',
            }}
            onClick={() => setForm({ ...form, neverExpires: true, expiryDate: null })}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <Avatar sx={{ bgcolor: form.neverExpires ? 'primary.main' : 'grey.300' }}><EventIcon /></Avatar>
              <Box flex={1}>
                <Typography fontWeight={600}>{t('Never Expires')}</Typography>
                <Typography variant="caption" color="text.secondary">{t('This coupon will remain active indefinitely')}</Typography>
              </Box>
              {form.neverExpires && <CheckCircleIcon color="primary" fontSize="small" />}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper
            variant="outlined"
            sx={{
              p: 2, cursor: 'pointer',
              borderColor: !form.neverExpires ? 'primary.main' : 'divider',
              borderWidth: 2,
              bgcolor: !form.neverExpires ? (theme) => alpha(theme.palette.primary.main, 0.05) : 'transparent',
            }}
            onClick={() => setForm({ ...form, neverExpires: false })}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <Avatar sx={{ bgcolor: !form.neverExpires ? 'primary.main' : 'grey.300' }}><EventIcon /></Avatar>
              <Box flex={1}>
                <Typography fontWeight={600}>{t('Set Expiry Date')}</Typography>
                <Typography variant="caption" color="text.secondary">{t('Choose a specific date and time')}</Typography>
              </Box>
              {!form.neverExpires && <CheckCircleIcon color="primary" fontSize="small" />}
            </Box>
          </Paper>
        </Grid>
        {!form.neverExpires && (
          <Grid item xs={12}>
            <FieldRow label={t('Expiry Date & Time')}>
              <TextField
                fullWidth
                type="datetime-local"
                value={form.expiryDate ? new Date(form.expiryDate).toISOString().slice(0, 16) : ''}
                onChange={(e) => setForm({ ...form, expiryDate: e.target.value ? new Date(e.target.value) : null })}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: new Date().toISOString().slice(0, 16) }}
              />
            </FieldRow>
          </Grid>
        )}
      </Grid>

      <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>{t('Preview')}</Typography>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          background: (theme) => `linear-gradient(135deg, ${alpha(form.color, 0.1)} 0%, ${alpha(theme.palette.background.paper, 0)} 100%)`,
          borderColor: form.color,
          borderStyle: 'dashed',
          borderWidth: 2,
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Box sx={{ width: 48, height: 48, borderRadius: `${radius.sm}px`, bgcolor: form.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <LocalOfferIcon sx={{ color: (theme) => theme.palette.getContrastText(form.color) }} />
          </Box>
          <Box flex={1} minWidth={0}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>{form.code || t('COUPON_CODE')}</Typography>
            <Typography variant="body2" color="success.main" fontWeight={600}>
              {form.discountType === 'PERCENT' ? `${form.discountValue || 0}% ${t('off')}` : `$${form.discountValue || 0} ${t('off')}`}
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
          <Chip size="small" icon={<InventoryIcon />} label={form.applicableProducts.length === 0 ? t('All Products') : `${form.applicableProducts.length} ${t('products')}`} variant="outlined" />
          <Chip size="small" icon={<PeopleIcon />} label={form.eligibleUsers.length === 0 ? t('All Users') : `${form.eligibleUsers.length} ${t('users selected')}`} variant="outlined" />
          <Chip size="small" icon={<EventIcon />} label={form.neverExpires ? t('Never expires') : (form.expiryDate ? new Date(form.expiryDate).toLocaleDateString() : t('Set expiry date'))} variant="outlined" color={form.neverExpires ? 'success' : 'default'} />
        </Stack>
      </Paper>
    </Box>
  );
};

export default CouponWizardDrawer;
